import type { EnvelopeMeta, PaginationQuery } from '@diora/contracts';
import { movedWithin } from '@diora/contracts';
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiGet, apiPatch, apiPost, apiPut } from '@/lib/api';
import { messageForError } from '@/lib/errors';
import { useToast } from '@/providers/toast';

/** A paged admin list, plus the `meta` a reorder needs. */
export function useAdminList<T>(key: QueryKey, url: string, params: object) {
  const query = useQuery({
    queryKey: key,
    queryFn: () => apiGet<T[]>(url, params),
  });

  return {
    ...query,
    rows: query.data?.data ?? [],
    meta: query.data?.meta as EnvelopeMeta | undefined,
    total: query.data?.meta?.total ?? 0,
    /** A BigInt as a string. Required to reorder the list. */
    contentRevision: query.data?.meta?.contentRevision,
  };
}

/** The query-string state every list screen keeps. */
export function useListParams(initial?: Partial<PaginationQuery>) {
  const [params, setParams] = useState<PaginationQuery>({
    page: 1,
    pageSize: 20,
    ...initial,
  });

  const update = useCallback((next: Partial<PaginationQuery>) => {
    // Any filter change resets to page 1 — staying on page 4 of a list that now
    // has one page shows an empty screen that looks like a bug.
    setParams((current) => ({
      ...current,
      ...next,
      page: 'page' in next ? (next.page ?? 1) : 1,
    }));
  }, []);

  return [params, update] as const;
}

/**
 * A mutation that reports itself.
 *
 * Success and failure both surface as a toast, and the error is translated from
 * its stable code rather than from the server's already-translated prose.
 */
export function useAdminMutation<TVariables, TData = unknown>(
  run: (variables: TVariables) => Promise<TData>,
  options: {
    successMessage?: string;
    invalidate?: QueryKey[];
    onSuccess?: (data: TData, variables: TVariables) => void;
    onError?: (error: unknown, variables: TVariables) => boolean | void;
  } = {},
) {
  const { t } = useTranslation();
  const { notify } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: run,
    onSuccess: (data, variables) => {
      for (const key of options.invalidate ?? []) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
      if (options.successMessage) notify(options.successMessage, 'success');
      options.onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      // A handler returning true has dealt with it — a confirmable refusal, say,
      // which opens a dialog rather than shouting.
      const handled = options.onError?.(error, variables);
      if (handled !== true) notify(messageForError(t, error), 'danger');
    },
  });
}

export interface LifecycleTarget {
  id: string;
  version: number;
}

/**
 * Publish, unpublish, archive and restore for any content resource.
 *
 * All four take the version the client last read, so two editors acting at once
 * produce a 409 rather than one silently undoing the other.
 */
export function useLifecycle(baseUrl: string, invalidate: QueryKey[]) {
  const { t } = useTranslation();

  const call = (action: string) => async ({ id, version, ...rest }: LifecycleTarget & Record<string, unknown>) =>
    apiPost(`${baseUrl}/${id}/${action}`, { expectedVersion: version, ...rest });

  const publish = useAdminMutation(call('publish'), {
    successMessage: t('publish.published'),
    invalidate,
  });
  const unpublish = useAdminMutation(call('unpublish'), {
    successMessage: t('publish.unpublished'),
    invalidate,
  });
  const archive = useAdminMutation(call('archive'), {
    successMessage: t('publish.archived'),
    invalidate,
  });
  const restore = useAdminMutation(call('restore'), {
    successMessage: t('publish.restored'),
    invalidate,
  });

  return {
    publish,
    unpublish,
    archive,
    restore,
    busy: publish.isPending || unpublish.isPending || archive.isPending || restore.isPending,
  };
}

/** One page is the most the API will hand over at a time. */
const MAX_PAGE_SIZE = 100;

/**
 * Every row the server will accept in a reorder, in its current order.
 *
 * The server's definition is "not deleted and not archived" and it refuses a
 * list that does not name all of them. The admin list is neither: it pages at
 * twenty, it narrows to whatever filter is set, and — with no status filter —
 * it *includes* archived rows. Assembling the payload from what happened to be
 * on screen therefore failed three different ways, and the screen had no way to
 * know which.
 */
async function fetchLiveOrder(url: string): Promise<string[]> {
  const ids: string[] = [];

  for (let page = 1; ; page += 1) {
    const response = await apiGet<Array<{ id: string; publicationStatus?: string }>>(url, {
      page,
      pageSize: MAX_PAGE_SIZE,
    });
    const rows = response.data ?? [];
    for (const row of rows) {
      if (row.publicationStatus !== 'ARCHIVED') ids.push(row.id);
    }

    const total = response.meta?.total ?? rows.length;
    if (rows.length === 0 || rows.length < MAX_PAGE_SIZE || page * MAX_PAGE_SIZE >= total) break;
  }

  return ids;
}

/**
 * Reordering a list.
 *
 * Guarded by the global content revision rather than any row's version: the
 * submitted IDs describe a whole list, and if anything was added or archived
 * since it was read, that list no longer exists. The revision sent is the one
 * that came with the page the editor was looking at — deliberately, not the
 * fresher one `fetchLiveOrder` just saw, because a list that moved underneath
 * them is exactly what this check is for.
 */
export function useReorder(url: string, invalidate: QueryKey[]) {
  const { t } = useTranslation();

  return useAdminMutation(
    async ({
      id,
      delta,
      visible,
      contentRevision,
    }: {
      id: string;
      delta: number;
      visible: string[];
      contentRevision: string;
    }) => {
      const live = await fetchLiveOrder(url);
      const ids = movedWithin(live, visible, id, delta);
      // Nothing moved: skip the write rather than bump the content revision and
      // leave an audit row saying an order changed when it did not.
      if (ids.every((entry, index) => entry === live[index])) return null;
      return apiPut(`${url}/order`, { ids, expectedContentRevision: contentRevision });
    },
    { successMessage: t('common.reordered'), invalidate },
  );
}

export { apiGet, apiPatch, apiPost, apiPut };
